import React, { useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    EditButton,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    ReferenceInput,
    FunctionField,
    required,
    email,
    useRecordContext,
    useNotify,
    useRedirect,
} from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Typography,
} from '@mui/material';
import { FormToolbar } from '../../common/FormToolbar';
import { APIUrl } from '../../common/apiConfig';
import { PasswordOnce } from '../school-teacher/PasswordOnce';
import { isSchoolAdmin } from '../../common/schoolRoles';

/**
 * Students.
 *
 * WHAT A TEACHER SEES HERE
 * ------------------------
 * Only students in the sections they are assigned to. A teacher with no
 * assignment sees an empty list -- not the school.
 *
 * That filtering is done by the API, in SQL, and this screen simply renders
 * what comes back. It is worth being explicit because these rows carry names,
 * guardian names and guardian phone numbers: this is the screen where the
 * teacher-versus-teacher boundary actually protects something.
 *
 * Writes are administrator-only. Moving a student between sections changes who
 * can see them, so it belongs to whoever runs the school rather than to whoever
 * teaches them.
 */

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`,
});

/** POST /school-student/{path}/{id}. Returns the result, or null after notifying the error. */
const postAction = async (notify: any, path: string, id: any, body: any = {}) => {
    try {
        const res = await fetch(`${APIUrl}school-student/${path}/${id}`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(body),
        });
        const json = await res.json();
        if (json.status !== 'success') {
            notify(json.message || 'That did not work.', { type: 'error' });
            return null;
        }
        notify(json.message, { type: 'success' });
        return json.result;
    } catch (e: any) {
        notify(e.message || 'That did not work.', { type: 'error' });
        return null;
    }
};

/**
 * Reset password straight from a list row, so the administrator does not have
 * to open the student first. The new password is shown once, in a dialog.
 */
const ResetPasswordButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [password, setPassword] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    if (!record) return null;

    const reset = async (e: React.MouseEvent) => {
        e.stopPropagation(); // the row itself opens the edit screen
        if (!window.confirm(`Reset the password for ${record.full_name}? Their old password stops working.`)) {
            return;
        }
        setBusy(true);
        const r = await postAction(notify, 'reset_password', record.id);
        setBusy(false);
        if (r && r.password_once) setPassword(r.password_once);
    };

    return (
        <>
            <Button size="small" onClick={reset} disabled={busy}>
                Reset password
            </Button>
            <Dialog
                open={!!password}
                onClick={e => e.stopPropagation()}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>New password for {record.full_name}</DialogTitle>
                <DialogContent>
                    <PasswordOnce password={password || ''} login={record.username} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPassword(null)}>I have written it down</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// =========================================================================
// List
// =========================================================================

const StudentList = props => {
    const admin = isSchoolAdmin();

    const filters = [
        <TextInput source="q" label="Search" alwaysOn resettable />,
        <ReferenceInput source="class_section_id" reference="class-section" label="Section">
            <SelectInput optionText="label" />
        </ReferenceInput>,
        <SelectInput
            source="status"
            label="Status"
            choices={[
                { id: 'active', name: 'Active' },
                { id: 'inactive', name: 'Left' },
                { id: 'transferred', name: 'Transferred' },
            ]}
        />,
    ];

    return (
        <List
            title="Students"
            {...props}
            filters={filters}
            filterDefaultValues={{ status: 'active' }}
            sort={{ field: 'full_name', order: 'ASC' }}
            hasCreate={admin}
            exporter={false}
            empty={
                <Alert severity="info" sx={{ m: 3 }}>
                    {admin
                        ? 'No students yet. Create a class section first, then add students to it.'
                        : 'You have no students yet. That means no class section has been assigned to you — ask your school administrator.'}
                </Alert>
            }
        >
            <Datagrid rowClick={admin ? 'edit' : false} bulkActionButtons={false}>
                {admin && <EditButton />}
                <TextField source="roll_number" label="Roll" />
                <TextField source="full_name" label="Name" />
                <TextField source="username" label="Login" sortable={false} />
                <FunctionField
                    label="Section"
                    sortBy="class_section_id"
                    render={(r: any) => <b>{r.section_label}</b>}
                />
                <TextField source="admission_no" label="Admission no." />
                <TextField source="guardian_name" label="Guardian" />
                <TextField source="guardian_phone" label="Guardian phone" />
                <FunctionField
                    label="Status"
                    sortBy="status"
                    render={(r: any) => (
                        <Chip
                            size="small"
                            label={r.status}
                            color={r.status === 'active' ? 'default' : 'error'}
                        />
                    )}
                />
                {admin && <ResetPasswordButton />}
            </Datagrid>
        </List>
    );
};

// =========================================================================
// Shared form fields
// =========================================================================

const StudentFields = () => (
    <>
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <TextInput
                source="full_name"
                label="Full name"
                validate={[required()]}
                sx={{ flex: '2 1 0', minWidth: 240 }}
            />
            <ReferenceInput source="class_section_id" reference="class-section" label="Section">
                <SelectInput
                    optionText="label"
                    validate={[required()]}
                    sx={{ flex: '1 1 0', minWidth: 180 }}
                    helperText="Which class they sit in"
                />
            </ReferenceInput>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <TextInput
                source="roll_number"
                label="Roll number"
                helperText="Must be unique within the section"
                sx={{ flex: 1 }}
            />
            <TextInput source="admission_no" label="Admission number" sx={{ flex: 1 }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <TextInput source="guardian_name" label="Guardian name" sx={{ flex: 2 }} />
            <TextInput source="guardian_phone" label="Guardian phone" sx={{ flex: 1 }} />
        </Box>
    </>
);

// =========================================================================
// Create
// =========================================================================

const StudentCreate = props => {
    const [created, setCreated] = useState<any>(null);

    if (created) {
        return (
            <Box sx={{ p: 3 }}>
                <PasswordOnce password={created.password_once} login={created.full_name} />
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <b>{created.full_name}</b> has been added to {created.section_label}.
                </Typography>
                <Button variant="contained" href="#/school-student/create">
                    Add another student
                </Button>
                <Button sx={{ ml: 2 }} href="#/school-student">
                    Back to the list
                </Button>
            </Box>
        );
    }

    return (
        <Create
            {...props}
            title="Add a student"
            mutationOptions={{ onSuccess: (d: any) => setCreated(d) }}
        >
            <SimpleForm toolbar={<FormToolbar {...props} />}>
                <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                    A password is generated and shown <b>once, on the next screen</b>. Write it down
                    before leaving the page. Adding students one at a time is fine for a handful
                    &mdash; a bulk import is coming for a whole roll.
                </Alert>

                <TextInput
                    source="username"
                    label="Login"
                    validate={[required(), email()]}
                    helperText="Their email address. Maximum 50 characters."
                    sx={{ maxWidth: 420 }}
                />

                <StudentFields />
            </SimpleForm>
        </Create>
    );
};

// =========================================================================
// Edit
// =========================================================================

const StudentActions = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [password, setPassword] = useState<string | null>(null);

    if (!record) return null;

    const post = (path: string, body: any) => postAction(notify, path, record.id, body);

    const resetPassword = async () => {
        const r = await post('reset_password', {});
        if (r && r.password_once) setPassword(r.password_once);
    };

    const remove = async () => {
        if (
            !window.confirm(
                `Remove ${record.full_name}? They will be signed out immediately. Their record ` +
                    `and marks are kept.`
            )
        ) {
            return;
        }
        if (await post('remove', {})) {
            window.location.hash = '#/school-student';
        }
    };

    return (
        <>
            {password && (
                <PasswordOnce
                    password={password}
                    login={record.username || record.full_name}
                    onDone={() => setPassword(null)}
                />
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Button variant="outlined" onClick={resetPassword}>
                    Reset password
                </Button>
                <Button variant="outlined" color="error" onClick={remove}>
                    Remove from school
                </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                Moving a student to another section changes which teachers can see them.
            </Typography>

            <Divider sx={{ my: 2, width: '100%' }} />
        </>
    );
};

const StudentEdit = props => {
    const notify = useNotify();
    const redirect = useRedirect();
    // Set when saving created a login for a student who had none. The API
    // returns its password once; show it before leaving the page.
    const [repaired, setRepaired] = useState<any>(null);

    if (repaired) {
        return (
            <Box sx={{ p: 3 }}>
                <PasswordOnce password={repaired.password_once} login={repaired.username} />
                <Button variant="contained" href="#/school-student">
                    Back to the list
                </Button>
            </Box>
        );
    }

    const onSuccess = (data: any) => {
        if (data && data.password_once) {
            setRepaired(data);
            return;
        }
        notify('Student updated', { type: 'success' });
        redirect('list', 'school-student');
    };

    return (
        <Edit {...props} title="Student" mutationMode="pessimistic" mutationOptions={{ onSuccess }}>
            <SimpleForm toolbar={<FormToolbar {...props} />}>
                <StudentActions />
                <TextInput
                    source="username"
                    label="Login"
                    validate={[required(), email()]}
                    helperText="Their email address. Changing it signs them out; they sign in again with the new login."
                    sx={{ maxWidth: 420 }}
                />
                <StudentFields />
            </SimpleForm>
        </Edit>
    );
};

export default { list: StudentList, create: StudentCreate, edit: StudentEdit };
